import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { Subscription } from 'rxjs';
import { FormFillFacade, FormFillUIState } from './form-fill.facade';
import { COMMON_IMPORTS } from '../../global-misc/common-imports';
import { ActivatedRoute } from '@angular/router';
import { ProgressBarModule } from 'primeng/progressbar';
import { SurveyModule } from 'survey-angular-ui';
import { Model, SurveyModel } from 'survey-core';

@Component({
  selector: 'app-form-fill',
  standalone: true,
  imports: [...COMMON_IMPORTS, ProgressBarModule, SurveyModule],
  templateUrl: './form-fill.component.html',
  styleUrls: ['./form-fill.component.css'],
})
export class FormFillComponent implements OnInit, OnDestroy {
  @Input() mode: 'public' | 'authenticated' = 'public';

  state: FormFillUIState = {
    state: 'idle',
    formData: null,
    isAuthenticated: false,
    assignmentId: null,
    accessToken: null,
    fillingMethod: null,
  };

  private sub = new Subscription();
  surveyModel: SurveyModel | null = null;

  constructor(
    private facade: FormFillFacade,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.sub.add(
      this.facade.state$.subscribe((state) => {
        console.log('State changed:', state);
        this.state = state;

        console.log('Checking conditions:');
        console.log('state.state === "ready":', state.state === 'ready');
        console.log('state.formData exists:', !!state.formData);
        console.log('!this.surveyModel:', !this.surveyModel);

        if (state.state === 'ready' && state.formData && !this.surveyModel) {
          console.log('Calling initializeSurvey...');
          this.initializeSurvey();
        }
      }),
    );

    // Set the mode
    this.facade.setMode(this.mode === 'authenticated');

    // Get assignment ID and token from route params and query params
    this.sub.add(
      this.route.paramMap.subscribe((params) => {
        const assignmentId = params.get('assignmentId');

        this.sub.add(
          this.route.queryParamMap.subscribe((queryParams) => {
            const accessToken = queryParams.get('token');
            const fillingMethod = queryParams.get('method');

            if (assignmentId && accessToken) {
              this.facade.initializeFormFill(assignmentId, accessToken, fillingMethod);
            }
          }),
        );
      }),
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.facade.reset();
  }

  private async initializeSurvey() {
    if (!this.state.formData) return;

    try {
      // Initialize Survey.js Model
      this.surveyModel = new Model(this.state.formData);

      // Ensure navigation buttons are enabled
      this.surveyModel.showNavigationButtons = true;
      this.surveyModel.showPrevButton = true;
      this.surveyModel.showCompleteButton = true;
      this.surveyModel.showPageNumbers = true;
      this.surveyModel.showQuestionNumbers = 'on';

      console.log('Survey model created:', this.surveyModel);
      console.log('Form data:', this.state.formData);

      // Check if there are any file upload questions
      const allQuestions = this.surveyModel.getAllQuestions();
      const fileQuestions = allQuestions.filter((q: any) => q.getType() === 'file');
      console.log('File upload questions found:', fileQuestions);
      console.log(
        'All questions:',
        allQuestions.map((q) => ({ name: q.name, type: q.getType() })),
      );

      // Configure each file question
      fileQuestions.forEach((question: any) => {
        console.log('Configuring file question:', question.name);
        console.log('Question properties:', question);
        question.allowMultiple = true;
        question.maxSize = 10 * 1024 * 1024; // 10MB
        question.acceptedTypes = 'image/*,application/pdf,.doc,.docx,.txt';
        question.storeDataAsText = false;
        question.waitForUpload = true;
      });

      // Configure file upload handler using the survey's uploadFiles method
      if (fileQuestions.length > 0) {
        console.log('Configuring global file upload handler...');

        // Override the survey's uploadFiles method
        this.surveyModel.uploadFiles = async (question: any, name: string, files: File[], callback: any) => {
          console.log('🔥 File upload triggered via uploadFiles method:', files);
          console.log('Question:', question);
          console.log('Name:', name);

          try {
            await this.handleFileUpload({ question, name, files, callback });
          } catch (error) {
            console.error('File upload error:', error);
            if (callback) {
              const errorMessage = error instanceof Error ? error.message : 'Upload failed';
              callback(null, [errorMessage]);
            }
          }
        };
      }

      // Note: File upload events may need to be handled differently in Survey.js 2.2.0
      // For now, we'll skip the file upload event handlers to avoid initialization errors
      console.log('⚠️ File upload events not configured for Survey.js 2.2.0');

      // Set up event handlers
      console.log('Setting up onComplete handler...');
      this.surveyModel.onComplete.add((survey) => {
        console.log('🎉 Survey onComplete event fired!', survey);
        this.onSurveyComplete(survey);
      });

      console.log('Setting up onValueChanged handler...');
      this.surveyModel.onValueChanged.add((survey) => {
        console.log('📝 Survey value changed:', survey.data);
        this.onSurveyValueChanged(survey);
      });

      // Add additional debugging event handlers
      this.surveyModel.onCurrentPageChanged.add((survey) => {
        console.log('📄 Survey page changed:', survey.currentPageNo);
      });

      this.surveyModel.onCompleting.add((survey) => {
        console.log('⏳ Survey completing event fired (before validation)!', survey);
        console.log('📝 Survey data before completion:', survey.data);
        console.log('🔍 Survey validation state:');

        // Check for validation errors
        const allQuestions = survey.getAllQuestions();
        console.log('📋 All questions:', allQuestions.length);

        allQuestions.forEach((question: any, index: number) => {
          console.log(`Question ${index + 1}:`, {
            name: question.name,
            title: question.title,
            isRequired: question.isRequired,
            isEmpty: question.isEmpty(),
            hasErrors: question.hasErrors(),
            errors: question.errors,
            value: question.value,
          });
        });

        // Check if survey is valid
        console.log('📊 Survey validation summary:');
        console.log('- hasErrors:', survey.hasErrors());
        console.log('- state:', survey.state);

        // This event fires before validation, return true to allow completion
        return true;
      });

      console.log('Survey initialized:', this.surveyModel);
      console.log('Survey pages count:', this.surveyModel.pageCount);
      console.log('Survey questions count:', this.surveyModel.getAllQuestions().length);

      // Additional debugging for survey rendering
      console.log('Survey mode:', this.surveyModel.mode);
      console.log('Survey showCompletedPage:', this.surveyModel.showCompletedPage);
      console.log('Survey showNavigationButtons:', this.surveyModel.showNavigationButtons);
      console.log('Survey showPrevButton:', this.surveyModel.showPrevButton);
      console.log('Survey showCompleteButton:', this.surveyModel.showCompleteButton);
    } catch (error) {
      console.error('Failed to initialize survey:', error);
    }
  }

  private async handleFileUpload(options: any) {
    const currentState = this.state;

    if (!currentState.assignmentId || !currentState.accessToken) {
      console.error('Missing assignment information for file upload');
      if (options.callback) {
        options.callback(null, ['Missing assignment information']);
      }
      return;
    }

    try {
      // Handle both the old options format and the new uploadFiles method format
      const files = options.files || [];

      const uploadPromises = files.map(async (file: File) => {
        const uploadResult = await this.facade.uploadFile(file, currentState.assignmentId!, currentState.accessToken!);

        if (uploadResult) {
          return {
            file: file,
            content: uploadResult, // This should be the file URL/path returned from upload
          };
        }
        throw new Error('Upload failed');
      });

      const results = await Promise.all(uploadPromises);

      // Call success callback with the uploaded file information
      if (options.callback) {
        options.callback(results, null);
      }
    } catch (error: any) {
      console.error('File upload error:', error);
      if (options.callback) {
        const errorMessage = error?.message || 'Upload failed';
        options.callback(null, [errorMessage]);
      }
    }
  }

  onSurveyComplete(survey: Model): void {
    console.log('🚀 onSurveyComplete called!');
    console.log('Survey:', survey);
    console.log('Survey data:', survey?.data);

    if (!survey) {
      console.error('❌ Survey is null or undefined in onSurveyComplete');
      return;
    }

    const responseData = survey.data;
    console.log('📋 Response data to submit:', responseData);

    console.log('🔄 Calling facade.submitForm...');
    this.facade.submitForm(responseData, 100, true);
  }

  onSurveyValueChanged(survey: Model): void {
    if (!survey) return;

    // Calculate progress based on answered questions
    const allQuestions = survey.getAllQuestions();
    const answeredQuestions = allQuestions.filter((q: any) => !q.isEmpty());
    const progress = allQuestions.length > 0 ? Math.round((answeredQuestions.length / allQuestions.length) * 100) : 0;

    this.facade.submitForm(survey.data, progress, false);
  }

  manualComplete(): void {
    console.log('🔧 Manual complete triggered for debugging');
    if (this.surveyModel) {
      console.log('📊 Current survey state:');
      console.log('- Data:', this.surveyModel.data);
      console.log('- Current page:', this.surveyModel.currentPageNo);
      console.log('- Is last page:', this.surveyModel.isLastPage);
      console.log('- State:', this.surveyModel.state);

      // Try to complete the survey manually
      console.log('🚀 Calling survey.doComplete()...');
      this.surveyModel.doComplete();
    } else {
      console.error('❌ No survey model available for manual completion');
    }
  }

  get isLoading(): boolean {
    return this.state.state === 'loading';
  }

  get isReady(): boolean {
    return this.state.state === 'ready';
  }

  get isExpired(): boolean {
    return this.state.state === 'expired';
  }

  get hasError(): boolean {
    return this.state.state === 'error';
  }

  get isSubmitted(): boolean {
    return this.state.state === 'submitted';
  }
}
